import React from 'react';

import { Alert, Card, Descriptions, Flex, Table, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { EpdContentChangeSchema, EpdDocumentSchema, EpdState } from '@app/models/orders';
import { formatDate, formatDateAndTime, fromUtc } from '@app/services/date';
import { RAW_INTERPOLATION } from '@app/services/i18n';
import { EpdFieldName } from '@components/EpdFieldName';
import { EpdIssues } from '@components/EpdIssues';
import { ISigningTarget } from '@components/EpdSigningModal/interfaces';
import { EpdStatusTag } from '@components/EpdStatusTag';

import { epdValue } from '../../services';
import { OperatorStatus } from '../OperatorStatus';
import { SigningRequest } from '../SigningRequest';
import { TitleCard } from '../TitleCard';

interface IProps {
  document: EpdDocumentSchema;
  awaitingShipment: boolean;
  onSign: (target: ISigningTarget) => void;
}

export const DocumentCard: React.FC<IProps> = ({ document, awaitingShipment, onSign }) => {
  const epdT = useTranslation(NSKeys.epd);
  const operator = document.operator_state;
  return (
    <Card
      title={`${epdT.t(`kinds.${document.kind}` as const)} · ${document.number} · ${formatDate(document.doc_date)}`}
      extra={
        <EpdStatusTag state={awaitingShipment ? EpdState.AwaitingShipment : document.state} kind={document.kind} />
      }
    >
      <Flex vertical gap={16}>
        <Descriptions
          size="small"
          column={1}
          items={[
            ...document.parties.map((party) => ({
              key: party.role,
              label: epdT.t(`parties.${party.role}` as const),
              children: party.name || '—',
            })),
            ...(operator?.status?.text
              ? [
                  {
                    key: 'operator_status',
                    label: epdT.t('operatorStatus'),
                    children: <OperatorStatus severity={operator.status.severity} text={operator.status.text} />,
                  },
                ]
              : []),
            ...(operator?.poa
              ? [
                  {
                    key: 'poa',
                    label: epdT.t('poaVerdict'),
                    children: (
                      <OperatorStatus
                        severity={operator.poa.severity}
                        text={operator.poa.text || operator.poa.status || operator.poa.error_code}
                      />
                    ),
                  },
                ]
              : []),
          ]}
        />
        {operator?.outer_statuses?.map((status, index) => (
          <Alert
            key={index}
            type={status.type === 'Error' ? 'error' : 'info'}
            showIcon
            title={status.text}
            description={
              <Flex vertical gap={4}>
                <Typography.Text>{[status.docflow, status.named_id].filter(Boolean).join(' · ')}</Typography.Text>
                {status.details.map((item, i) => (
                  <Typography.Text key={i}>{epdValue(item)}</Typography.Text>
                ))}
              </Flex>
            }
          />
        ))}
        {document.state === EpdState.Error &&
          document.errors.map((error, index) => (
            <Alert
              key={index}
              type="error"
              showIcon
              title={epdValue(error.text || error.code)}
              description={error.details ? epdValue(error.details) : undefined}
            />
          ))}
        {document.discrepancy && (
          <Card
            size="small"
            title={epdT.t('discrepancyAt', {
              date: formatDateAndTime(fromUtc(document.discrepancy.at)),
              ...RAW_INTERPOLATION,
            })}
          >
            {document.discrepancy.comparable ? (
              <Table<EpdContentChangeSchema>
                size="small"
                rowKey="field"
                pagination={false}
                dataSource={document.discrepancy.fields}
                columns={[
                  {
                    key: 'field',
                    dataIndex: 'field',
                    title: epdT.t('field'),
                    render: (value: string) => <EpdFieldName path={value} />,
                  },
                  { key: 'before', dataIndex: 'before', title: epdT.t('before'), render: epdValue },
                  { key: 'after', dataIndex: 'after', title: epdT.t('after'), render: epdValue },
                ]}
              />
            ) : (
              <EpdIssues issues={document.discrepancy.issues} />
            )}
          </Card>
        )}
        {document.titles.map((title) => (
          <TitleCard key={title.uid} title={title} operatorState={operator?.titles?.[title.title]} />
        ))}
        {!!document.requests.length && <Typography.Title level={5}>{epdT.t('requests')}</Typography.Title>}
        {document.requests.map((request) => (
          <SigningRequest
            key={request.uid}
            request={request}
            onSign={() => onSign({ uid: request.uid, number: document.number, title: request.title })}
          />
        ))}
      </Flex>
    </Card>
  );
};
